/**
 * Formatage des dates pour affichage UI (JJ/MM/AAAA).
 *
 * Stockage DB conforme OH-AUDIT-005 : ISO YYYY-MM-DD ou ISO datetime
 * (cf. CC-06). Cette couche d'affichage convertit l'ISO en format FR.
 *
 * Utiliser :
 *   - formatDateFR(value)     → "24/02/2026"
 *   - formatDateTimeFR(value) → "24/02/2026 14:25"
 *
 * Inputs tolérés :
 *   - ISO date         "2025-09-19"
 *   - ISO datetime     "2026-03-10T14:25:03+00:00" / "...Z"
 *   - FR padded        "19/09/2025"  → tel quel
 *   - FR sans padding  "2/1/2025"    → "02/01/2025" (normalisé)
 *   - Date object
 *   - null / undefined / ""           → "-"
 *   - Texte non parsable               → tel quel (legacy data)
 */

const FALLBACK = "-";

function _pad2(n) {
  const s = String(n);
  return s.length === 1 ? `0${s}` : s;
}

function _parseToDate(value) {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const s = String(value).trim();
  if (!s) return null;

  // ISO YYYY-MM-DD strict
  const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (isoDateMatch) {
    const [, y, m, d] = isoDateMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  // ISO datetime (avec T)
  if (s.includes("T")) {
    const dt = new Date(s);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // FR (DD/MM/YYYY, D/M/YYYY, séparateurs /, -, .)
  const frMatch = /^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/.exec(s);
  if (frMatch) {
    const [, d, m, y] = frMatch;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }

  return null;
}

/**
 * Formate une date en JJ/MM/AAAA. Retourne "-" si vide, ou la valeur
 * brute si non parsable (preserve les valeurs legacy comme "EN COURS").
 */
export function formatDateFR(value) {
  if (value === null || value === undefined || value === "") return FALLBACK;
  const dt = _parseToDate(value);
  if (!dt) return String(value); // legacy non parsable conservé
  return `${_pad2(dt.getDate())}/${_pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
}

/**
 * Formate une date+heure en JJ/MM/AAAA HH:MM. Pour les timestamps
 * d'audit (date_creation, date_archivage). Si l'input est une date
 * sans heure, retourne juste la date.
 */
export function formatDateTimeFR(value) {
  if (value === null || value === undefined || value === "") return FALLBACK;
  const dt = _parseToDate(value);
  if (!dt) return String(value);
  const dateStr = `${_pad2(dt.getDate())}/${_pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
  // Si l'input n'avait pas d'heure (ISO date strict ou FR), pas d'heure dans l'output
  const s = String(value);
  const hasTime = s instanceof Date ? true : /T\d{2}:\d{2}/.test(s);
  if (!hasTime) return dateStr;
  return `${dateStr} ${_pad2(dt.getHours())}:${_pad2(dt.getMinutes())}`;
}
